import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, MessageSquare, DollarSign, Zap, PiggyBank, Database, ThumbsUp, Star } from "lucide-react";

const COLORS = {
  primaryDark: "#08263e",
  primaryRed: "#ec474e",
  secondaryBlue: "#518dca",
};

export const MetricCards = ({ analytics }) => {
  if (!analytics) return null;

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: `${COLORS.secondaryBlue}20` }}>
                <Users size={20} style={{ color: COLORS.secondaryBlue }} />
              </div>
              <div>
                <p className="text-sm text-[#666]">Sessions</p>
                <p className="text-2xl font-bold" data-testid="metric-sessions">{analytics.total_sessions}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: `${COLORS.primaryDark}15` }}>
                <MessageSquare size={20} style={{ color: COLORS.primaryDark }} />
              </div>
              <div>
                <p className="text-sm text-[#666]">Messages</p>
                <p className="text-2xl font-bold" data-testid="metric-messages">{analytics.total_messages}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: `${COLORS.secondaryBlue}20` }}>
                <Zap size={20} style={{ color: COLORS.secondaryBlue }} />
              </div>
              <div>
                <p className="text-sm text-[#666]">Total Tokens</p>
                <p className="text-2xl font-bold">{(analytics.total_input_tokens + analytics.total_output_tokens).toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: `${COLORS.primaryRed}20` }}>
                <DollarSign size={20} style={{ color: COLORS.primaryRed }} />
              </div>
              <div>
                <p className="text-sm text-[#666]">Total Cost</p>
                <p className="text-2xl font-bold" data-testid="total-cost-usd">${analytics.total_cost.toFixed(4)}</p>
                {analytics.total_cost_php !== undefined && (
                  <p className="text-xs text-[#999]" data-testid="total-cost-php">~PHP {analytics.total_cost_php.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-green-200" style={{ backgroundColor: '#f0fdf4' }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-200 rounded-lg">
                <PiggyBank size={20} className="text-green-700" />
              </div>
              <div>
                <p className="text-sm text-green-700">FAQ Optimization Savings</p>
                <p className="text-2xl font-bold text-green-800" data-testid="cost-saved-usd">${analytics.cost_saved_by_faq.toFixed(4)}</p>
                {analytics.cost_saved_by_faq_php !== undefined && (
                  <p className="text-xs text-green-600" data-testid="cost-saved-php">~PHP {analytics.cost_saved_by_faq_php.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: `${COLORS.secondaryBlue}20` }}>
                <Database size={20} style={{ color: COLORS.secondaryBlue }} />
              </div>
              <div>
                <p className="text-sm text-[#666]">FAQ Hits (Free)</p>
                <p className="text-2xl font-bold" data-testid="metric-faq-hits">{analytics.faq_hits}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: `${COLORS.primaryRed}20` }}>
                <Zap size={20} style={{ color: COLORS.primaryRed }} />
              </div>
              <div>
                <p className="text-sm text-[#666]">LLM Calls (Paid)</p>
                <p className="text-2xl font-bold" data-testid="metric-llm-calls">{analytics.llm_calls}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={analytics.ratings?.satisfaction_rate >= 70 ? "border-green-200" : analytics.ratings?.satisfaction_rate >= 40 ? "border-yellow-200" : analytics.ratings?.total_rated > 0 ? "border-red-200" : ""}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: "#dbeafe" }}>
                <ThumbsUp size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-[#666]">In-Chat Thumbs</p>
                <p className="text-2xl font-bold" data-testid="satisfaction-rate">
                  {analytics.ratings?.total_rated > 0 ? `${analytics.ratings.satisfaction_rate}%` : "\u2014"}
                </p>
                <p className="text-[10px] text-[#999]" data-testid="rating-counts">
                  <span className="text-green-600">{analytics.ratings?.thumbs_up || 0}</span>
                  {" / "}
                  <span className="text-red-500">{analytics.ratings?.thumbs_down || 0}</span>
                  {" "}({analytics.ratings?.total_rated || 0} rated)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mb-6">
        {analytics.survey && analytics.survey.total_responses > 0 && (
          <Card className="border-yellow-200" style={{ backgroundColor: '#fefce8' }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Star size={20} className="text-yellow-600 fill-yellow-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-yellow-700">Post-Chat Survey Rating</p>
                  <div className="flex items-center gap-3">
                    <p className="text-2xl font-bold text-yellow-800" data-testid="survey-avg-rating">{analytics.survey.average_rating}</p>
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map(s => (
                        <Star key={s} size={14} className={s <= Math.round(analytics.survey.average_rating) ? "text-yellow-500 fill-yellow-500" : "text-yellow-200"} />
                      ))}
                    </div>
                    <span className="text-xs text-yellow-600">from {analytics.survey.total_responses} response{analytics.survey.total_responses !== 1 ? 's' : ''}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {analytics.exchange_rate && (
        <div className="bg-white border border-dashed border-[#ccc] rounded-lg px-4 py-2.5 mb-6 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-[#888]" data-testid="pricing-disclaimer">
          <span className="font-medium text-[#555]">Pricing basis:</span>
          <span>Emergent credit rate: 6,000 credits / $1,000 (~$0.167/credit)</span>
          <span>USD/PHP: {analytics.exchange_rate.usd_php}</span>
          <span>Source: {analytics.exchange_rate.source}</span>
          {analytics.exchange_rate.last_updated && (
            <span>Updated: {new Date(analytics.exchange_rate.last_updated).toLocaleDateString()}</span>
          )}
          <span className="italic text-[#aaa]">PHP values are approximate and based on daily market exchange rates</span>
        </div>
      )}
    </>
  );
};
